import { useState } from "react";
import type { FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { APP_NAME, BrandMark } from "../config/brand";
import { login } from "../lib/api/auth";
import { ApiError } from "../lib/api/client";
import { useAuth } from "../providers/AuthProvider";
import { Button } from "../components/ui/Button";

/** Login lands each role on its home dashboard (PRD §4). */
const homeFor = (role: string | undefined): string =>
  role === "admin" ? "/analytics" : "/";

export function LoginPage() {
  const navigate = useNavigate();
  const { status, user, config, refresh } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const appName = config?.appName ?? APP_NAME;

  // Already authenticated → skip straight to the role's home dashboard.
  if (status === "authed") {
    return <Navigate to={homeFor(user?.role)} replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) {
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const { user: loggedIn } = await login(username, password);
      await refresh();
      navigate(homeFor(loggedIn.role), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "INVALID_CREDENTIALS") {
        setError("Incorrect username or password.");
        setPassword("");
      } else if (err instanceof ApiError && err.code === "TOO_MANY_ATTEMPTS") {
        setError(err.message);
        setPassword("");
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center px-gutter py-12">
      <div className="w-full max-w-sm rounded-lg border border-surface-border bg-surface p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <BrandMark className="h-9 w-9 text-brand-600" />
          <span className="text-brand font-semibold text-text">{appName}</span>
        </div>

        <h1 className="text-title font-semibold text-text">Sign in</h1>
        <p className="mt-1 text-body text-text-muted">
          Enter your credentials to access your files.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4" noValidate>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="username" className="text-body font-medium text-text">
              Username
            </label>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-md border border-surface-border bg-surface px-3 py-2 text-body text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="password" className="text-body font-medium text-text">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-surface-border bg-surface px-3 py-2 text-body text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            />
          </div>

          <Button type="submit" variant="primary" disabled={submitting} className="mt-1 w-full">
            {submitting ? "Signing in…" : "Sign in"}
          </Button>

          <div aria-live="polite" className="min-h-[1.25rem]">
            {error ? <p className="text-body text-danger-700">{error}</p> : null}
          </div>
        </form>
      </div>
    </div>
  );
}
